interface PopupOptions {
  width: number;
  height: number;
  top?: number;
  left?: number;
}
interface ProviderData {
  name: string;
  clientId?: string;
  url: string;
  redirectUri: string;
  defaultUrlParams?: string[];
  requiredUrlParams?: string[];
  optionalUrlParams?: string[];
  responseMode?: string;
  scope?: string[];
  scopeDelimiter: string;
  scopePrefix?: string;
  authorizationEndpoint: string;
  nonceGenerationEndpoint?: string;
  responseParams: Record<string, string>;
  popupOptions: PopupOptions;
  [key: string]: any;
}

export { PopupOptions, ProviderData };

export default class FederatedAuthConfig {
  private static createGoogleProvider(siteUrl?: string): ProviderData {
    return {
      name: 'google',
      url: 'api/auth/provider/google',
      authorizationEndpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
      nonceGenerationEndpoint: `${siteUrl}/api/auth/generateNonce/google`,
      redirectUri: `${siteUrl}/api/auth/oauthCallback.html`,
      defaultUrlParams: ['response_type', 'client_id', 'redirect_uri'],
      requiredUrlParams: ['scope', 'state', 'nonce'],
      optionalUrlParams: ['display', 'access_type', 'prompt'],
      scope: ['profile', 'email'],
      scopePrefix: 'openid',
      scopeDelimiter: ' ',
      responseType: 'code',
      responseParams: {
        code: 'code',
        clientId: 'clientId',
        redirectUri: 'redirectUri',
        nonce: 'nonce',
      },
      popupOptions: { width: 452, height: 633 },
    };
  }

  private static createAppleProvider(siteUrl?: string): ProviderData {
    return {
      name: 'apple',
      url: 'api/auth/provider/apple',
      authorizationEndpoint: 'https://appleid.apple.com/auth/authorize',
      nonceGenerationEndpoint: `${siteUrl}/api/auth/generateNonce/apple`,
      redirectUri: `${siteUrl}/api/auth/appleLogin`,
      defaultUrlParams: ['response_type', 'client_id', 'redirect_uri', 'response_mode'],
      requiredUrlParams: ['display', 'scope', 'state', 'nonce'],
      optionalUrlParams: ['prompt'],
      scope: ['email', 'name'],
      scopeDelimiter: ' ',
      responseType: 'code',
      responseMode: 'form_post',
      responseParams: {
        code: 'code',
        clientId: 'clientId',
        redirectUri: 'redirectUri',
      },
      popupOptions: { width: 880, height: 800 },
    };
  }

  private static createFacebookProvider(siteUrl?: string): ProviderData {
    return {
      name: 'facebook',
      url: 'api/auth/provider/facebook',
      authorizationEndpoint: 'https://www.facebook.com/v24.0/dialog/oauth',
      nonceGenerationEndpoint: `${siteUrl}/api/auth/generateNonce/facebook`,
      redirectUri: `${siteUrl}/api/auth/oauthCallback.html`,
      defaultUrlParams: ['response_type', 'client_id', 'redirect_uri'],
      requiredUrlParams: ['display', 'scope', 'state', 'nonce'],
      optionalUrlParams: ['prompt'],
      scope: ['email', 'openid'],
      scopeDelimiter: ',',
      responseType: 'code',
      responseParams: {
        code: 'code',
        clientId: 'clientId',
        redirectUri: 'redirectUri',
        nonce: 'nonce',
        stayLoggedInFacebook: 'stayLoggedInFacebook',
      },
      popupOptions: { width: 580, height: 600 },
    };
  }

  public static get(name: string, siteUrl?: string): any {
    const providers: Array<ProviderData> = [
      FederatedAuthConfig.createGoogleProvider(siteUrl),
      FederatedAuthConfig.createFacebookProvider(siteUrl),
      FederatedAuthConfig.createAppleProvider(siteUrl),
    ];
    return providers.find(provider => provider.name === name);
  }
}
